import { createContext, useContext, useState, useCallback, useMemo } from "react";

const DEFAULT_STATE = {
  step: 1,
  address: null,              // { display_name, lat, lon, address: {country_code, ...} }
  countryCode: null,          // "BE" | "NL" | "LU" | ...
  roofPolygon: [],            // [[lat, lng], ...]
  panels: [],                 // [{ id, centerLatLng, cornersLatLng }]
  removedPanelIds: new Set(),
  globalAzimuth: 180,
  globalTilt: 35,
  simulationId: null,
  // Phase 3
  selectedPanel: null,        // catalogue item
  selectedInverter: null,     // catalogue item
  selectedBattery: null,      // catalogue item (or null = "no battery")
  annualConsumptionKwh: 4200, // household default
  computed: null,             // { production, pricing, economics }
};

export const SimulatorContext = createContext(null);

export function SimulatorProvider({ children }) {
  const [state, setState] = useState(DEFAULT_STATE);

  const patch = useCallback((update) => {
    setState((s) => ({ ...s, ...(typeof update === "function" ? update(s) : update) }));
  }, []);

  const goNext = useCallback(() => {
    setState((s) => ({ ...s, step: Math.min(s.step + 1, 5) }));
  }, []);
  const goPrev = useCallback(() => {
    setState((s) => ({ ...s, step: Math.max(s.step - 1, 1) }));
  }, []);
  const goTo = useCallback((step) => {
    setState((s) => ({ ...s, step: Math.max(1, Math.min(5, step)) }));
  }, []);

  const reset = useCallback(() => setState(DEFAULT_STATE), []);

  const value = useMemo(
    () => ({ ...state, patch, goNext, goPrev, goTo, reset }),
    [state, patch, goNext, goPrev, goTo, reset]
  );

  return <SimulatorContext.Provider value={value}>{children}</SimulatorContext.Provider>;
}

export function useSimulator() {
  const ctx = useContext(SimulatorContext);
  if (!ctx) throw new Error("useSimulator must be used within <SimulatorProvider>");
  return ctx;
}
